import { useState, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { mutate } from '../lib/dbMutate'
import { X, Zap, Shield } from 'lucide-react'
import { isDevMode } from '../lib/devMode'
import { TIDEditSection, blankTID, validateTID, buildTIDPayload } from './TIDSection'

const C = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' },
  modal: { background: '#0a140a', border: '1px solid #00ff66', boxShadow: '0 0 20px rgba(0,255,102,.2), inset 0 0 30px rgba(0,255,102,.04)', width: '100%', maxWidth: '420px', padding: '24px', position: 'relative', color: '#00ff66', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.5)', maxHeight: '90vh', overflowY: 'auto' },
  input: { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '10px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', outline: 'none', textShadow: '0 0 4px rgba(0,255,102,.3)' },
  label: { color: '#4aaa5a', fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px', display: 'block', fontFamily: "'JetBrains Mono', monospace" },
  btnPrimary: { width: '100%', fontFamily: "'VT323', monospace", fontSize: '16px', letterSpacing: '0.1em', textTransform: 'uppercase', background: '#00ff66', color: '#000', border: 'none', padding: '12px 20px', cursor: 'pointer', boxShadow: '0 0 8px #00ff66', textShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  typeBtn: (active) => ({ padding: '8px', fontFamily: "'VT323', monospace", fontSize: '14px', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', background: active ? '#00ff66' : 'rgba(0,20,8,.4)', color: active ? '#000' : '#4aaa5a', border: active ? 'none' : '1px solid rgba(0,255,102,.18)', textShadow: active ? 'none' : '0 0 4px rgba(0,255,102,.3)', boxShadow: active ? '0 0 6px #00ff66' : 'none' }),
  toggle: (on) => ({ position: 'relative', width: 40, height: 20, borderRadius: 10, background: on ? '#00ff66' : 'rgba(0,255,102,.15)', border: '1px solid rgba(0,255,102,.3)', cursor: 'pointer' }),
  toggleDot: (on) => ({ position: 'absolute', top: 2, left: on ? 20 : 2, width: 14, height: 14, borderRadius: '50%', background: on ? '#000' : '#4aaa5a', transition: 'left .2s' }),
  dropdown: { position: 'absolute', zIndex: 10, width: '100%', marginTop: 4, background: '#0a140a', border: '1px solid rgba(0,255,102,.3)', maxHeight: 192, overflowY: 'auto' },
  dropdownItem: { width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', color: '#00ff66', cursor: 'pointer', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.3)' },
  hint: { color: '#2a6a3a', fontSize: '10px', marginTop: '4px', fontFamily: "'JetBrains Mono', monospace" },
  sectionLabel: { color: '#00ff66', fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'VT323', monospace", marginBottom: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,255,102,.15)' },
}

export default function IssueBounty({ session, players, user, userFactionId, factions = [], checkpoints = [], onClose, onIssued, inline = false, allowBountyStacking = false }) {
  const [targetId, setTargetId] = useState('')
  const [title, setTitle] = useState('')
  const [points, setPoints] = useState(100)
  const [notes, setNotes] = useState('')
  const [hasExpiry, setHasExpiry] = useState(false)
  const [expiryMinutes, setExpiryMinutes] = useState(15)
  const [loading, setLoading] = useState(false)
  const [issueAsQueued, setIssueAsQueued] = useState(false)
  const [bountyType, setBountyType] = useState('kill')
  const [targetSearch, setTargetSearch] = useState('')
  const [showTargetList, setShowTargetList] = useState(false)
  const [targetName, setTargetName] = useState('')

  // Defend-specific state
  const [prepDuration, setPrepDuration] = useState(5)
  const [activeDuration, setActiveDuration] = useState(30)
  const [hasBleedout, setHasBleedout] = useState(false)
  const [bleedoutSeconds, setBleedoutSeconds] = useState(60)
  const [autoGenKillBounty, setAutoGenKillBounty] = useState(true)

  // Stacking modal state
  const [showStackModal, setShowStackModal] = useState(false)
  const [stackDupe, setStackDupe] = useState(null) // { bountyId, currentPoints }
  const [stackPointsToAdd, setStackPointsToAdd] = useState(100)
  const [stackReason, setStackReason] = useState('')
  const [stackBusy, setStackBusy] = useState(false)

  // ANU-88 — T-ID
  const [tid, setTid] = useState(blankTID())
  const tidUploadFolder = useRef(`${session?.id || 'tmp'}/t_id/${crypto.randomUUID()}`).current

  // Protect-specific state
  const [protectMode, setProtectMode] = useState('survive')
  const [minProtectors, setMinProtectors] = useState(1)
  const [protectDuration, setProtectDuration] = useState(30)
  const [destCpId, setDestCpId] = useState('')
  const [bonusTimeMinutes, setBonusTimeMinutes] = useState('')
  const [bonusPoints, setBonusPoints] = useState('')

  const handleIssue = async () => {
    if (!targetId) return
    const tidErr = validateTID(tid)
    if (tidErr) { alert(tidErr); return }
    setLoading(true)

    // Universal duplicate check — same type + target + event.
    // KILL+PROTECT coexistence is explicitly allowed; all other same-type
    // duplicates either block (stacking off) or open the raise-points modal.
    const { data: dupes } = await supabase
      .from('bounties')
      .select('id, points')
      .eq('event_id', session.id)
      .eq('target_id', targetId)
      .eq('bounty_type', bountyType)
      .in('status', ['active', 'queued', 'registration'])
      .limit(1)
    if (dupes?.[0]) {
      const callsign = targetName ? targetName.split(' — ')[0] : 'target'
      if (!allowBountyStacking) {
        alert(`An active ${bountyType} bounty already exists on ${callsign}. Cancel it before creating a new one.`)
        setLoading(false)
        return
      }
      // Stacking enabled — show raise-points modal instead of creating a new bounty
      setStackDupe({ bountyId: dupes[0].id, currentPoints: dupes[0].points })
      setStackPointsToAdd(parseInt(points))
      setShowStackModal(true)
      setLoading(false)
      return
    }

    const bountyData = {
      event_id: session.id,
      target_id: targetId,
      issued_by: user.id,
      title: title.trim() || null,
      points: parseInt(points),
      bounty_type: bountyType,
      notes,
      status: issueAsQueued ? 'queued' : 'active',
      category: 'bounty',
      target_type: 'player',
    }

    // Kill bounty fields
    if (bountyType === 'kill') {
      bountyData.phase = 'active'
      bountyData.expires_at = hasExpiry ? new Date(Date.now() + expiryMinutes * 60000).toISOString() : null
    }

    // Defend bounty fields
    if (bountyType === 'defend') {
      bountyData.phase = 'prep'
      bountyData.prep_duration = parseInt(prepDuration)
      bountyData.active_duration = parseInt(activeDuration)
      bountyData.bleedout_duration = hasBleedout ? parseInt(bleedoutSeconds) : null
      bountyData.phase_started_at = new Date().toISOString()
    }

    // Escort bounty fields (same as kill for now)
    if (bountyType === 'escort') {
      bountyData.phase = 'active'
      bountyData.expires_at = hasExpiry ? new Date(Date.now() + expiryMinutes * 60000).toISOString() : null
    }

    // Protect bounty fields
    if (bountyType === 'protect') {
      bountyData.status = issueAsQueued ? 'queued' : 'registration'
      bountyData.protect_mode = protectMode
      bountyData.vip_player_id = targetId
      bountyData.min_protectors = parseInt(minProtectors) || 1
      if (protectMode === 'survive') {
        bountyData.active_duration = parseInt(protectDuration) || 30
      } else {
        bountyData.destination_cp_id = destCpId || null
        if (bonusTimeMinutes) bountyData.bonus_time_minutes = parseInt(bonusTimeMinutes)
        if (bonusPoints) bountyData.bonus_points = parseInt(bonusPoints)
      }
    }

    // Track 1.2 — mutate() flushes the bounties cache on success so the
    // PlayerView bounty board picks up the new row on its next SWR read
    // instead of falling back to the pre-insert snapshot. Track 5.2
    // surfaces the friendly error translation via result.friendlyMessage
    // (consumed below where the legacy code surfaced error.message).
    const result = await mutate('bounties', () =>
      supabase.from('bounties').insert(bountyData).select('id').limit(1)
    )
    const { data: bounty, error } = result
    if (error) {
      setError(result.friendlyMessage || 'Failed to issue bounty.')
      setLoading(false)
      return
    }
    if (!error && bounty?.[0]) {
      // Auto-generate hidden kill bounty for opposition if defend type
      if (bountyType === 'defend' && autoGenKillBounty && !issueAsQueued) {
        const { data: killBounty } = await supabase.from('bounties').insert({
          event_id: session.id,
          target_id: targetId,
          issued_by: user.id,
          points: Math.round(parseInt(points) * 0.5),
          bounty_type: 'kill',
          category: 'bounty',
          phase: 'active',
          status: 'active',
          target_type: 'player',
          linked_bounty_id: bounty[0].id,
          notes: 'Auto-generated from defend bounty',
          // ANU-80 — inherit the defend's active window so the kill bounty
          // expires when the protection window ends (server trigger stamps
          // expires_at from active_duration at insert time).
          active_duration: parseInt(activeDuration),
        }).select('id').limit(1)

        // Link both ways
        if (killBounty?.[0]) {
          await supabase.from('bounties').update({ linked_bounty_id: killBounty[0].id }).eq('id', bounty[0].id)
        }

        // Log mission event
        await supabase.from('mission_events').insert({
          bounty_id: bounty[0].id,
          event_type: 'phase_change',
          triggered_by: user.id,
          notes: `Defend bounty created. Prep phase: ${prepDuration}min. Active phase: ${activeDuration}min.`,
        })
      }

      // ANU-88 — insert T-ID dossier entry if authored
      const tidPayload = buildTIDPayload(tid, {
        eventId: session.id,
        parentType: 'bounty',
        parentId: bounty[0].id,
        authorId: user.id,
      })
      if (tidPayload) {
        const { error: tidErr } = await supabase.from('dossier_entries').insert(tidPayload)
        if (tidErr) console.warn('[T-ID] dossier_entries insert failed', tidErr)
      }

      if (!isDevMode) {
        try {
          await supabase.functions.invoke('send-push-notification', {
            body: {
              target_user_id: targetId,
              title: '🎯 Bounty Issued',
              body: `${points} pts — ${bountyType}${notes ? ': ' + notes : ''}`,
              url: '/play'
            }
          })
        } catch (err) { console.log('Push failed:', err) }
      }
      onIssued()
    }
    setLoading(false)
  }

  const handleRaisePoints = async () => {
    if (stackBusy) return
    const addPts = parseInt(stackPointsToAdd) || 0
    if (addPts <= 0) { alert('Points added must be greater than 0.'); return }
    setStackBusy(true)
    const { data: curr } = await supabase.from('bounties').select('points').eq('id', stackDupe.bountyId).single()
    if (!curr) { alert('Bounty not found.'); setStackBusy(false); return }
    const prevPts = curr.points
    const newPts = prevPts + addPts
    const { error: upErr } = await supabase.from('bounties').update({ points: newPts }).eq('id', stackDupe.bountyId)
    if (upErr) { alert('Failed to raise points: ' + upErr.message); setStackBusy(false); return }
    await supabase.from('bounty_stack_log').insert({
      bounty_id: stackDupe.bountyId,
      added_by: user.id,
      points_added: addPts,
      reason: stackReason.trim() || null,
      previous_points: prevPts,
      new_points: newPts,
    })
    if (!isDevMode) {
      try {
        await supabase.functions.invoke('send-push-notification', {
          body: { target_user_id: targetId, title: '📈 Bounty Raised', body: `+${addPts} pts — ${bountyType} bounty now worth ${newPts} pts`, url: '/play' }
        })
      } catch {}
    }
    setShowStackModal(false)
    setStackBusy(false)
    onIssued()
  }

  // Form body — shared between modal and inline modes.
  const formBody = (
    <>
        {/* Corner brackets */}
        <div style={{ position:'absolute',top:-1,left:-1,width:10,height:10,borderTop:'2px solid #9aff9a',borderLeft:'2px solid #9aff9a' }}/>
        <div style={{ position:'absolute',bottom:-1,right:-1,width:10,height:10,borderBottom:'2px solid #9aff9a',borderRight:'2px solid #9aff9a' }}/>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Zap size={20} style={{ color: '#00ff66' }} />
            <h2 style={{ ...C.display, fontSize: '20px', margin: 0 }}>ISSUE BOUNTY</h2>
          </div>
          {/* Track 6.2 — 44x44 min hit area on the modal close. */}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer', minHeight: 44, minWidth: 44, padding: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Type */}
          <div>
            <label style={C.label}>TYPE</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              {[
                { key: 'kill',    label: '🎯 KILL' },
                { key: 'defend',  label: '⚔️ DEFEND' },
                { key: 'protect', label: '🛡 PROTECT' },
              ].map(t => (
                <button key={t.key}
                  onClick={() => { if (!t.disabled) setBountyType(t.key) }}
                  disabled={t.disabled}
                  title={t.disabled ? t.disabledHint : undefined}
                  style={{
                    ...C.typeBtn(bountyType === t.key),
                    ...(t.disabled ? { opacity: 0.4, cursor: 'not-allowed', boxShadow: 'none', textShadow: 'none' } : {}),
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
            {bountyType === 'protect' && <p style={C.hint}>VIP must opt-in to activate the protection window.</p>}
          </div>

          {/* Target */}
          <div style={{ position: 'relative' }}>
            <label style={C.label}>{bountyType === 'defend' || bountyType === 'protect' ? 'VIP TARGET' : 'TARGET'}</label>
            <input type="text" value={targetName || targetSearch} onChange={(e) => { setTargetSearch(e.target.value); setTargetName(''); setTargetId(''); setTid(blankTID()); setShowTargetList(true) }} onFocus={() => setShowTargetList(true)} placeholder={bountyType === 'defend' || bountyType === 'protect' ? 'Search VIP callsign or name...' : 'Search callsign or name...'} style={C.input} maxLength={60} />
            {showTargetList && targetSearch.length > 0 && (
              <div style={C.dropdown} className="modal-scroll">
                {(() => {
                  // UX cleanup — large rosters now sort by faction name
                  // then callsign so the dropdown reads predictably. The
                  // existing search filter still runs first.
                  const factionName = (fid) => factions.find(f => f.id === fid)?.name || '~unassigned'
                  const sorted = [...players]
                    .filter(p => { if (p.user_id === user.id) return false; if (isDevMode) return true; if (factions.length === 0 || !userFactionId) return true; if (bountyType === 'protect') return true; return bountyType === 'defend' ? p.faction_id === userFactionId : p.faction_id !== userFactionId })
                    .filter(p => { const s = targetSearch.toLowerCase(); return (p.users?.callsign || '').toLowerCase().includes(s) || (p.users?.display_name || '').toLowerCase().includes(s) })
                    .sort((a, b) => {
                      const fa = factionName(a.faction_id), fb = factionName(b.faction_id)
                      if (fa !== fb) return fa.localeCompare(fb)
                      return (a.users?.callsign || a.users?.display_name || '').localeCompare(b.users?.callsign || b.users?.display_name || '')
                    })
                  return sorted
                })().map(p => (
                  <button key={p.user_id} onClick={() => { setTargetId(p.user_id); setTargetName(`${p.users?.callsign || p.users?.display_name} — ${p.users?.display_name || ''}`); setTargetSearch(''); setShowTargetList(false) }} style={C.dropdownItem}
                    onMouseEnter={e => e.target.style.background = 'rgba(0,255,102,.08)'} onMouseLeave={e => e.target.style.background = 'none'}>
                    <span style={{ color: '#00ff66' }}>{p.users?.callsign || '—'}</span>
                    <span style={{ color: '#4aaa5a', marginLeft: 8 }}>{p.users?.display_name}</span>
                    {factions.find(f => f.id === p.faction_id) && <span style={{ color: '#2a6a3a', marginLeft: 8, fontSize: 10 }}>{factions.find(f => f.id === p.faction_id)?.name}</span>}
                  </button>
                ))}
              </div>
            )}
            {bountyType === 'defend' && <p style={C.hint}>Defend targets are filtered to YOUR faction (friendly VIP)</p>}
            {bountyType === 'protect' && <p style={C.hint}>VIP can be any faction — all players shown</p>}
          </div>

          {/* ANU-171 — T-ID inline below target picker */}
          {targetId && (
            <TIDEditSection
              tid={tid}
              onChange={setTid}
              eventId={session?.id}
              uploadFolder={tidUploadFolder}
              disabled={loading}
            />
          )}

          {/* Points */}
          <div>
            <label style={C.label}>POINTS: <span style={{ color: '#00ff66', fontFamily: "'VT323', monospace", fontSize: 16 }}>{points}</span></label>
            <input type="range" min="50" max="1000" step="50" value={points} onChange={(e) => setPoints(e.target.value)} style={{ width: '100%', accentColor: '#00ff66' }} />
            <div className="flex justify-between" style={{ fontSize: 10, color: '#2a6a3a' }}><span>50</span><span>1000</span></div>
          </div>

          {/* === KILL / ESCORT FIELDS === */}
          {(bountyType === 'kill' || bountyType === 'escort') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label style={C.label}>TIME LIMIT</label>
                <div onClick={() => setHasExpiry(!hasExpiry)} style={C.toggle(hasExpiry)}><div style={C.toggleDot(hasExpiry)} /></div>
              </div>
              {hasExpiry && (
                <div className="flex items-center gap-3">
                  <input type="number" value={expiryMinutes} onChange={(e) => setExpiryMinutes(e.target.value)} style={{ ...C.input, width: 96 }} maxLength={100} />
                  <span style={{ color: '#4aaa5a', fontSize: 12 }}>minutes</span>
                </div>
              )}
            </div>
          )}

          {/* === DEFEND FIELDS === */}
          {bountyType === 'defend' && (
            <>
              <div style={C.sectionLabel}>
                <Shield size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                DEFEND PARAMETERS
              </div>

              {/* Prep Duration */}
              <div>
                <label style={C.label}>PREP PHASE</label>
                <div className="flex items-center gap-3">
                  <input type="number" value={prepDuration} onChange={(e) => setPrepDuration(e.target.value)} min={1} max={60} style={{ ...C.input, width: 96 }} />
                  <span style={{ color: '#4aaa5a', fontSize: 12 }}>minutes</span>
                </div>
                <p style={C.hint}>Guards scan VIP to check in during this window</p>
              </div>

              {/* Active Duration */}
              <div>
                <label style={C.label}>ACTIVE PHASE</label>
                <div className="flex items-center gap-3">
                  <input type="number" value={activeDuration} onChange={(e) => setActiveDuration(e.target.value)} min={5} max={480} style={{ ...C.input, width: 96 }} />
                  <span style={{ color: '#4aaa5a', fontSize: 12 }}>minutes</span>
                </div>
                <p style={C.hint}>How long VIP must survive</p>
              </div>

              {/* Bleedout */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label style={C.label}>BLEEDOUT TIMER</label>
                  <div onClick={() => setHasBleedout(!hasBleedout)} style={C.toggle(hasBleedout)}><div style={C.toggleDot(hasBleedout)} /></div>
                </div>
                {hasBleedout && (
                  <div className="flex items-center gap-3">
                    <input type="number" value={bleedoutSeconds} onChange={(e) => setBleedoutSeconds(e.target.value)} min={10} max={300} style={{ ...C.input, width: 96 }} />
                    <span style={{ color: '#4aaa5a', fontSize: 12 }}>seconds</span>
                  </div>
                )}
                <p style={C.hint}>{hasBleedout ? 'Medic window before VIP death is confirmed' : 'No bleedout — VIP death is immediate'}</p>
              </div>

              {/* Auto-gen kill bounty */}
              <div className="flex items-center justify-between">
                <div>
                  <label style={C.label}>AUTO-GENERATE KILL ORDER</label>
                  <p style={C.hint}>Creates a hidden kill bounty on VIP for the opposing faction</p>
                </div>
                <div onClick={() => setAutoGenKillBounty(!autoGenKillBounty)} style={C.toggle(autoGenKillBounty)}><div style={C.toggleDot(autoGenKillBounty)} /></div>
              </div>
            </>
          )}

          {/* === PROTECT FIELDS === */}
          {bountyType === 'protect' && (
            <>
              <div style={C.sectionLabel}>
                <Shield size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                PROTECT PARAMETERS
              </div>

              {/* Mode */}
              <div>
                <label style={C.label}>MODE</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {[
                    { key: 'survive', label: 'SURVIVE', disabled: false },
                    { key: 'escort', label: 'ESCORT (post-Sept)', disabled: true },
                  ].map(m => (
                    <button key={m.key} type="button" disabled={m.disabled}
                      onClick={() => !m.disabled && setProtectMode(m.key)}
                      title={m.disabled ? 'Escort mode not available until post-September rework' : undefined}
                      style={{ ...C.typeBtn(protectMode === m.key), ...(m.disabled ? { opacity: 0.35, cursor: 'not-allowed', boxShadow: 'none' } : {}) }}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Min Protectors */}
              <div>
                <label style={C.label}>MIN PROTECTORS</label>
                <div className="flex items-center gap-3">
                  <input type="number" value={minProtectors} onChange={e => setMinProtectors(e.target.value)} min={1} max={20} style={{ ...C.input, width: 96 }} />
                  <span style={{ color: '#4aaa5a', fontSize: 12 }}>required before VIP can activate</span>
                </div>
              </div>

              {/* SURVIVE: Duration */}
              {protectMode === 'survive' && (
                <div>
                  <label style={C.label}>SURVIVE DURATION</label>
                  <div className="flex items-center gap-3">
                    <input type="number" value={protectDuration} onChange={e => setProtectDuration(e.target.value)} min={5} max={480} style={{ ...C.input, width: 96 }} />
                    <span style={{ color: '#4aaa5a', fontSize: 12 }}>minutes</span>
                  </div>
                  <p style={C.hint}>VIP + protectors earn points when timer expires</p>
                </div>
              )}

              {/* ESCORT: Destination CP + Bonus */}
              {protectMode === 'escort' && (
                <>
                  <div>
                    <label style={C.label}>DESTINATION CHECKPOINT</label>
                    <select value={destCpId} onChange={e => setDestCpId(e.target.value)} style={C.input}>
                      <option value="">— Select checkpoint —</option>
                      {checkpoints.map(cp => (
                        <option key={cp.id} value={cp.id}>{cp.name || cp.label || cp.id.slice(0, 8)}</option>
                      ))}
                    </select>
                    <p style={C.hint}>VIP scans this CP to complete the escort</p>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={C.label}>BONUS TIME WINDOW (min)</label>
                      <input type="number" value={bonusTimeMinutes} onChange={e => setBonusTimeMinutes(e.target.value)} min={1} placeholder="optional" style={C.input} />
                    </div>
                    <div>
                      <label style={C.label}>BONUS POINTS</label>
                      <input type="number" value={bonusPoints} onChange={e => setBonusPoints(e.target.value)} min={1} placeholder="optional" style={C.input} />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Title */}
          <div>
            <label style={C.label}>TITLE <span style={{ color: '#2a6a3a' }}>(optional)</span></label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Eliminate the Informant" maxLength={80} style={C.input} />
          </div>

          {/* Notes */}
          <div>
            <label style={C.label}>GM NOTES <span style={{ color: '#2a6a3a' }}>(optional)</span></label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={bountyType === 'defend' ? 'VIP mission objective details...' : 'Target is armed and dangerous'} maxLength={500} style={C.input} />
          </div>

          {/* Queued toggle */}
          <div className="flex items-center justify-between">
            <label style={C.label}>ISSUE AS QUEUED</label>
            <div onClick={() => setIssueAsQueued(!issueAsQueued)} style={C.toggle(issueAsQueued)}><div style={C.toggleDot(issueAsQueued)} /></div>
          </div>


          <button onClick={handleIssue} disabled={loading || !targetId} style={{ ...C.btnPrimary, opacity: loading || !targetId ? 0.5 : 1 }}>
            <Zap size={16} /> {loading ? 'ISSUING...' : bountyType === 'defend' ? 'ISSUE DEFEND ORDER' : bountyType === 'protect' ? 'ISSUE PROTECT ORDER' : 'ISSUE BOUNTY'}
          </button>
        </div>
    </>
  )

  const stackModal = showStackModal && stackDupe && (
    <div style={{ ...C.overlay, zIndex: 60 }}>
      <div style={{ ...C.modal, maxWidth: 380 }} className="modal-scroll">
        <div style={{ position:'absolute',top:-1,left:-1,width:10,height:10,borderTop:'2px solid #ffcc33',borderLeft:'2px solid #ffcc33' }}/>
        <div style={{ position:'absolute',bottom:-1,right:-1,width:10,height:10,borderBottom:'2px solid #ffcc33',borderRight:'2px solid #ffcc33' }}/>
        <h2 style={{ ...C.display, fontSize: 18, color: '#ffcc33', marginBottom: 12 }}>⚠ DUPLICATE BOUNTY</h2>
        <p style={{ ...C.mono, fontSize: 11, color: '#4aaa5a', marginBottom: 16 }}>
          {targetName ? targetName.split(' — ')[0] : 'Target'} already has an active{' '}
          <span style={{ color: '#ffcc33', textTransform: 'uppercase' }}>{bountyType}</span> bounty worth{' '}
          <span style={{ color: '#ffcc33' }}>{stackDupe.currentPoints} pts</span>.{' '}
          Raise point value instead?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={C.label}>POINTS TO ADD</label>
            <input type="number" min={1} value={stackPointsToAdd} onChange={e => setStackPointsToAdd(e.target.value)}
              style={C.input} />
            <p style={C.hint}>New total: {stackDupe.currentPoints + (parseInt(stackPointsToAdd) || 0)} pts</p>
          </div>
          <div>
            <label style={C.label}>REASON <span style={{ color: '#2a6a3a' }}>(optional)</span></label>
            <input type="text" value={stackReason} onChange={e => setStackReason(e.target.value)}
              placeholder="Intel upgrade, escalation..." maxLength={200} style={C.input} />
          </div>
          <button onClick={handleRaisePoints} disabled={stackBusy} style={{ ...C.btnPrimary, background: '#ffcc33', boxShadow: '0 0 8px #ffcc33', opacity: stackBusy ? 0.5 : 1 }}>
            {stackBusy ? 'RAISING...' : '↑ RAISE POINTS'}
          </button>
          <button onClick={() => setShowStackModal(false)} style={{ background: 'none', border: '1px solid rgba(0,255,102,.2)', color: '#4aaa5a', padding: '10px', cursor: 'pointer', ...C.mono, fontSize: 12 }}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  )

  // Inline mode renders the form body in a panel without the fixed-overlay
  // modal — for embedding directly inside a tab.
  if (inline) {
    return (
      <>
        <div className="modal-scroll" style={{
          background: '#0a140a',
          border: '1px solid rgba(0,255,102,.4)',
          boxShadow: '0 0 8px rgba(0,255,102,.1)',
          padding: '20px',
          marginBottom: '16px',
          position: 'relative',
          color: '#00ff66',
          fontFamily: "'JetBrains Mono', monospace",
          textShadow: '0 0 4px rgba(0,255,102,.5)',
        }}>
          {formBody}
        </div>
        {stackModal}
      </>
    )
  }

  return (
    <>
      <div style={C.overlay}>
        <div style={C.modal} className="modal-scroll">
          {formBody}
        </div>
      </div>
      {stackModal}
    </>
  )
}
